// src/pages/EmailTemplatesPage.js — [marker: email-compose v1] + create / upload HTML / delete custom
import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillResize from 'quill-resize-module';
import 'quill-resize-module/dist/resize.css';

// Preserve image width/height/style so a drag-resize survives save -> reload.
const _BaseImage = Quill.import('formats/image');
const _IMG_ATTRS = ['alt', 'height', 'width', 'style'];
class _ResizableImage extends _BaseImage {
  static formats(domNode) {
    return _IMG_ATTRS.reduce((acc, a) => {
      if (domNode.hasAttribute(a)) acc[a] = domNode.getAttribute(a);
      return acc;
    }, {});
  }
  format(name, value) {
    if (_IMG_ATTRS.indexOf(name) > -1) {
      if (value) this.domNode.setAttribute(name, value);
      else this.domNode.removeAttribute(name);
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(_ResizableImage, true);
Quill.register('modules/resize', QuillResize);

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'align',
  'link', 'image', 'width', 'height', 'style', 'alt',
];

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // template row, or { id: null } for a new one

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete the template "${t.template_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/email-templates/${t.id}`);
      toast.success('Template deleted.');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete.');
    }
  };

  const reload = () => {
    setLoading(true);
    api.get('/email-templates')
      .then(r => setTemplates(r.data || []))
      .catch(() => toast.error('Failed to load templates.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Email Templates</h1>
          <p className="page-subtitle">
            Built-in templates are used by the system's emails. Your own templates (New / Upload) can be picked
            whenever you review an email before sending.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing({ id: null })}>+ New Template</button>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Template Name</th><th>Subject</th><th>Key</th><th>Status</th><th>Updated</th><th style={{ width: 110 }}></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></td></tr>
            : templates.length === 0 ? <tr><td colSpan={6}><div className="empty-state"><div className="empty-state-icon">📧</div><h3>No templates configured</h3></div></td></tr>
            : templates.map(t => (
              <tr key={t.id}>
                <td className="td-primary">{t.template_name}</td>
                <td style={{ fontSize: '0.875rem' }}>{t.subject}</td>
                <td><code style={{ fontSize: '0.75rem', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{t.template_key}</code></td>
                <td>{t.is_active ? <span className="badge badge-active">Active</span> : <span className="badge badge-lapsed">Inactive</span>}</td>
                <td style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{fmtDate(t.updated_at)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(t)}>Edit</button>
                  {String(t.template_key || '').startsWith('custom_') && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', marginLeft: 4 }} onClick={() => handleDelete(t)} title="Delete this custom template">✕</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditTemplateModal
          templateId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function EditTemplateModal({ templateId, onClose, onSaved }) {
  const [tpl, setTpl] = useState(null);
  const [form, setForm] = useState({
    templateName: '', subject: '', bodyHtml: '',
    signatureBlock: '', footerText: '', isActive: true,
  });
  const isNew = !templateId;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const htmlFileRef = useRef(null);
  // Designed emails (tables, inline styles) are kept exactly as-is in HTML code view;
  // the visual editor would flatten them.
  const [codeView, setCodeView] = useState(false);

  // Upload an .html file into the body (only the <body> part is kept; <style>/<script> are dropped)
  const handleHtmlFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.html?$/i.test(file.name)) { toast.error('Choose an .html file.'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('That file is over 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      let text = String(reader.result || '');
      const t = text.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
      const m = text.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (m) text = m[1];
      text = text.replace(/<script[\s\S]*?<\/script>/gi, '').replace(/<style[\s\S]*?<\/style>/gi, '');
      setCodeView(true);
      setForm(f => ({
        ...f,
        bodyHtml: text,
        templateName: f.templateName || file.name.replace(/\.html?$/i, ''),
        subject: f.subject || (t ? t[1].trim() : ''),
      }));
      toast.success('HTML loaded into the body — review it, then save.');
    };
    reader.readAsText(file);
  };
  const [preview, setPreview] = useState(null);
  const quillRef = useRef(null);

  // Toolbar image button -> upload to a hosted URL (so the image loads in inboxes),
  // then insert it as an <img> the user can click to drag-resize (quill-resize-module).
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image() {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/png,image/jpeg,image/gif');
          input.onchange = async () => {
            const file = input.files && input.files[0];
            if (!file) return;
            try {
              const fd = new FormData();
              fd.append('image', file);
              const res = await api.post('/email-templates/upload-image', fd, {
                headers: { 'Content-Type': undefined },
              });
              const editor = quillRef.current && quillRef.current.getEditor();
              if (!editor) return;
              const range = editor.getSelection(true) || { index: editor.getLength() };
              editor.insertEmbed(range.index, 'image', res.data.url, 'user');
              editor.setSelection(range.index + 1, 0);
              toast.success('Image inserted — click it to resize.');
            } catch (err) {
              toast.error(err.response?.data?.error || 'Image upload failed.');
            }
          };
          input.click();
        },
      },
    },
    resize: { locale: {}, modules: ['Resize', 'DisplaySize'] },
  }), []);

  useEffect(() => {
    if (!templateId) return;
    api.get(`/email-templates/${templateId}`)
      .then(res => {
        setTpl(res.data);
        setForm({
          templateName:   res.data.template_name || '',
          subject:        res.data.subject || '',
          bodyHtml:       res.data.body_html || '',
          /* open designed (table-based) templates in code view so nothing is lost */
          signatureBlock: res.data.signature_block || '',
          footerText:     res.data.footer_text || '',
          isActive:       res.data.is_active !== false,
        });
      })
      .catch(() => toast.error('Failed to load template.'))
      .finally(() => setLoading(false));
  }, [templateId]);
  useEffect(() => { if (/<table/i.test(form.bodyHtml || '')) setCodeView(true); /* eslint-disable-next-line */ }, [tpl]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.subject.trim() || !form.bodyHtml.trim()) {
      toast.error('Subject and body are required.');
      return;
    }
    if (isNew && !form.templateName.trim()) { toast.error('Give the template a name.'); return; }
    setSaving(true);
    try {
      if (isNew) await api.post('/email-templates', form);
      else await api.patch(`/email-templates/${templateId}`, form);
      toast.success(isNew ? 'Template created.' : 'Template saved.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleShowPreview = async () => {
    // Save current form to template, then fetch the rendered preview
    try {
      await api.patch(`/email-templates/${templateId}`, form);
      const res = await api.post(`/email-templates/${templateId}/preview`);
      setPreview(res.data);
      setShowPreview(true);
    } catch (err) {
      toast.error('Failed to preview.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <span className="modal-title">
            {isNew ? 'New Template' : 'Edit Template'}
            {tpl && <span style={{ color: 'var(--gray-400)', marginLeft: 8, fontSize: '0.8rem', fontWeight: 400 }}>
              {tpl.template_key}
            </span>}
          </span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div> : showPreview && preview ? (
            <div>
              <div style={{ background: 'var(--gray-50)', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginBottom: 4 }}>Subject:</div>
                <div style={{ fontWeight: 600 }}>{preview.subject}</div>
              </div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 16, background: 'white', minHeight: 300, maxHeight: 500, overflowY: 'auto' }}
                   dangerouslySetInnerHTML={{ __html: preview.html }} />
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => setShowPreview(false)}>
                ← Back to Edit
              </button>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Template Name</label>
                <input className="form-input" value={form.templateName} onChange={e => set('templateName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input className="form-input" value={form.subject} onChange={e => set('subject', e.target.value)} />
                <p className="form-hint">You can use placeholders like {'{{first_name}}'} — they'll be filled in at send time.</p>
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="form-label">Body *</label>
                  <span>
                    <input ref={htmlFileRef} type="file" accept=".html,.htm,text/html" style={{ display: 'none' }} onChange={handleHtmlFile} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => htmlFileRef.current && htmlFileRef.current.click()}
                            title="Load an .html email design (e.g. exported from Mailchimp or a designer)">⬆ Upload .html</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                      if (codeView && /<table/i.test(form.bodyHtml || '') && !window.confirm('The visual editor will simplify this design (tables and layout are lost). Switch anyway?')) return;
                      setCodeView(v => !v);
                    }}>{codeView ? '✎ Visual editor' : '</> HTML code'}</button>
                  </span>
                </div>
                {codeView && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <textarea className="form-textarea" value={form.bodyHtml} onChange={e => set('bodyHtml', e.target.value)}
                              style={{ fontFamily: 'monospace', fontSize: '0.78rem', minHeight: 320 }} spellCheck={false} />
                    <iframe title="Preview" srcDoc={form.bodyHtml} sandbox="" style={{ width: '100%', minHeight: 320, border: '1px solid var(--gray-200)', borderRadius: 6, background: 'white' }} />
                  </div>
                )}
                <div className="njjba-tpl-editor" style={{ background: 'white', borderRadius: 6, display: codeView ? 'none' : 'block' }}>
                  {!codeView && <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.bodyHtml}
                    onChange={value => set('bodyHtml', value)}
                    modules={quillModules}
                    formats={QUILL_FORMATS}
                    placeholder="Write the email body. Use the image button to add a picture, then click it to drag-resize."
                  />}
                </div>
                <p className="form-hint">
                  Click the image button in the toolbar to insert a hosted image (it loads in inboxes). Click an inserted image to drag its corners and resize. You can still use placeholders like {'{{first_name}}'} — they'll be filled in at send time.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Signature Block</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={form.signatureBlock}
                  onChange={e => set('signatureBlock', e.target.value)}
                  placeholder="e.g. Warm regards,<br/>NJJBA Team"
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Footer Text</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={form.footerText}
                  onChange={e => set('footerText', e.target.value)}
                  placeholder="e.g. NJJBA · Newark, NJ · unsubscribe link"
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
                  <span>Active (sends use this template)</span>
                </label>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {!showPreview && (
            <>
              {!isNew && (
                <button className="btn btn-secondary" onClick={handleShowPreview} disabled={loading || saving}>
                  👁 Preview
                </button>
              )}
              <button className="btn btn-primary" onClick={handleSave} disabled={loading || saving}>
                {saving ? 'Saving…' : (isNew ? 'Create Template' : 'Save Changes')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
